import { useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { Card } from '@/components/ui/Card'
import { Spinner } from '@/components/ui/Spinner'
import {
  useNotifications,
  useNotificationsStats,
  useMarkNotificationAsRead,
  useMarkAllNotificationsAsRead,
} from '@/hooks/useNotifications'
import {
  notificationTypeLabels,
  notificationTypeColors,
} from '@/types/notification'
import type { Notification, NotificationType } from '@/types/notification'
import {
  Search,
  Bell,
  AlertTriangle,
  MessageSquare,
  FileText,
  CreditCard,
  FolderKanban,
  Info,
  Check,
  Filter,
  Trash2,
  ListChecks,
} from 'lucide-react'

const defaultColors = { bg: 'bg-beige-100', icon: 'text-beige-600' }

const getNotificationIcon = (type: NotificationType) => {
  switch (type) {
    case 'ticket': return MessageSquare
    case 'devis': return FileText
    case 'payment': return CreditCard
    case 'project': return FolderKanban
    case 'reminder': return AlertTriangle
    case 'system':
    default: return Info
  }
}

const formatTimeAgo = (dateString: string): string => {
  const date = new Date(dateString)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)

  if (diffMins < 1) return "A l'instant"
  if (diffMins < 60) return `Il y a ${diffMins} min`
  if (diffHours < 24) return `Il y a ${diffHours}h`
  if (diffDays < 7) return `Il y a ${diffDays}j`
  return date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

type FilterType = NotificationType | 'all'
type FilterRead = 'all' | 'unread' | 'read'

export default function NotificationsPage() {
  const navigate = useNavigate()
  const [searchQuery, setSearchQuery] = useState('')
  const [typeFilter, setTypeFilter] = useState<FilterType>('all')
  const [readFilter, setReadFilter] = useState<FilterRead>('all')
  const [showFilters, setShowFilters] = useState(false)

  const { notifications, isLoading, deleteNotification, isDeleting } = useNotifications()
  const { data: stats } = useNotificationsStats()
  const markAsRead = useMarkNotificationAsRead()
  const markAllAsRead = useMarkAllNotificationsAsRead()

  const filteredNotifications = useMemo(() => {
    return notifications.filter((n: Notification) => {
      if (typeFilter !== 'all' && n.type !== typeFilter) return false
      if (readFilter === 'unread' && n.read) return false
      if (readFilter === 'read' && !n.read) return false
      if (searchQuery) {
        const q = searchQuery.toLowerCase()
        return (
          (n.title?.toLowerCase().includes(q) ?? false) ||
          n.message.toLowerCase().includes(q)
        )
      }
      return true
    })
  }, [notifications, typeFilter, readFilter, searchQuery])

  const handleNotificationClick = (notification: Notification) => {
    if (!notification.read) {
      markAsRead.mutateAsync(notification.id)
    }

    if (notification.link) {
      navigate(notification.link)
    }
  }

  const handleMarkAllAsRead = async () => {
    try {
      await markAllAsRead.mutateAsync()
    } catch (error) {
      console.error('Erreur:', error)
    }
  }

  const handleDelete = async (e: React.MouseEvent, id: string) => {
    e.stopPropagation()
    try {
      await deleteNotification(id)
    } catch (error) {
      console.error('Erreur:', error)
    }
  }

  // Get unique types from notifications for filter
  const availableTypes = useMemo(() => {
    const types = new Set(notifications.map((n: Notification) => n.type))
    return Array.from(types) as NotificationType[]
  }, [notifications])

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-brown-900">Notifications</h1>
          <p className="text-brown-600 mt-1">
            {stats ? `${stats.unread} non lue${stats.unread > 1 ? 's' : ''} sur ${stats.total} notification${stats.total > 1 ? 's' : ''}` : 'Chargement...'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {stats && stats.unread > 0 && (
            <button
              onClick={handleMarkAllAsRead}
              disabled={markAllAsRead.isPending}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-mint-700 bg-mint-50 hover:bg-mint-100 rounded-lg transition-colors disabled:opacity-50"
            >
              <Check className="w-4 h-4" />
              Tout marquer lu
            </button>
          )}
        </div>
      </div>

      {/* Stats cards */}
      {stats && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-beige-100 flex items-center justify-center">
                <Bell className="w-5 h-5 text-beige-600" />
              </div>
              <div>
                <p className="text-sm text-brown-500">Total</p>
                <p className="text-xl font-bold text-brown-900">{stats.total}</p>
              </div>
            </div>
          </Card>
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-mint-100 flex items-center justify-center">
                <ListChecks className="w-5 h-5 text-mint-600" />
              </div>
              <div>
                <p className="text-sm text-brown-500">Non lues</p>
                <p className="text-xl font-bold text-brown-900">{stats.unread}</p>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Search and Filters */}
      <Card className="p-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-brown-400" />
            <input
              type="text"
              placeholder="Rechercher dans les notifications..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-beige-200 rounded-lg text-sm focus:ring-2 focus:ring-mint-500 focus:border-mint-500 outline-none"
            />
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
              showFilters ? 'bg-mint-100 text-mint-700' : 'bg-beige-50 text-brown-700 hover:bg-beige-100'
            }`}
          >
            <Filter className="w-4 h-4" />
            Filtres
          </button>
        </div>

        {showFilters && (
          <div className="flex flex-wrap gap-3 mt-3 pt-3 border-t border-beige-200">
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value as FilterType)}
              className="px-3 py-2 border border-beige-200 rounded-lg text-sm focus:ring-2 focus:ring-mint-500 focus:border-mint-500 outline-none"
            >
              <option value="all">Tous les types</option>
              {availableTypes.map((type) => (
                <option key={type} value={type}>
                  {notificationTypeLabels[type] || type}
                </option>
              ))}
            </select>

            <select
              value={readFilter}
              onChange={(e) => setReadFilter(e.target.value as FilterRead)}
              className="px-3 py-2 border border-beige-200 rounded-lg text-sm focus:ring-2 focus:ring-mint-500 focus:border-mint-500 outline-none"
            >
              <option value="all">Toutes</option>
              <option value="unread">Non lues</option>
              <option value="read">Lues</option>
            </select>

            {(typeFilter !== 'all' || readFilter !== 'all') && (
              <button
                onClick={() => { setTypeFilter('all'); setReadFilter('all') }}
                className="px-3 py-2 text-sm text-brown-500 hover:text-brown-700 transition-colors"
              >
                Reinitialiser
              </button>
            )}
          </div>
        )}
      </Card>

      {/* Notifications List */}
      <Card className="overflow-hidden">
        {isLoading ? (
          <div className="py-12 text-center">
            <Spinner />
          </div>
        ) : filteredNotifications.length === 0 ? (
          <div className="py-12 text-center">
            <Bell className="w-12 h-12 mx-auto mb-3 text-beige-300" />
            <p className="text-brown-500 font-medium">Aucune notification</p>
            <p className="text-sm text-brown-400 mt-1">
              {searchQuery || typeFilter !== 'all' || readFilter !== 'all'
                ? 'Aucun resultat avec ces filtres'
                : 'Vous etes a jour !'}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-beige-100">
            {filteredNotifications.map((notification: Notification) => {
              const Icon = getNotificationIcon(notification.type)
              const colors = notificationTypeColors[notification.type] || defaultColors

              return (
                <div
                  key={notification.id}
                  onClick={() => handleNotificationClick(notification)}
                  className={`flex items-start gap-4 px-5 py-4 cursor-pointer hover:bg-beige-50 transition-colors ${
                    !notification.read ? 'bg-mint-50/30' : ''
                  }`}
                >
                  {/* Icon */}
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${colors.bg}`}>
                    <Icon className={`w-5 h-5 ${colors.icon}`} />
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-center gap-2 min-w-0">
                        <p className={`text-sm text-brown-900 truncate ${!notification.read ? 'font-semibold' : 'font-medium'}`}>
                          {notification.title || notification.message}
                        </p>
                        {!notification.read && (
                          <span className="w-2 h-2 rounded-full bg-mint-500 flex-shrink-0" />
                        )}
                      </div>
                      <span className="text-xs text-brown-400 whitespace-nowrap flex-shrink-0">
                        {formatTimeAgo(notification.created_at)}
                      </span>
                    </div>
                    {notification.title && (
                      <p className="text-sm text-brown-600 mt-0.5 line-clamp-2">
                        {notification.message}
                      </p>
                    )}
                    <div className="flex items-center gap-2 mt-2">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${colors.bg} ${colors.icon}`}>
                        {notificationTypeLabels[notification.type] || notification.type}
                      </span>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-1 flex-shrink-0">
                    {!notification.read && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          markAsRead.mutateAsync(notification.id)
                        }}
                        className="p-1.5 text-brown-400 hover:text-mint-600 hover:bg-mint-50 rounded-lg transition-colors"
                        title="Marquer comme lu"
                      >
                        <Check className="w-4 h-4" />
                      </button>
                    )}
                    <button
                      onClick={(e) => handleDelete(e, notification.id)}
                      disabled={isDeleting}
                      className="p-1.5 text-brown-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                      title="Supprimer"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </Card>
    </div>
  )
}
